import React, { FC, useEffect, useState } from 'react';
import { Row, Col, Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import TermsAndConditionsModal from '@web/components/terms-and-conditions-modal';
import { TERMS_AND_CONDITIONS } from '@web/views/termsAndConditions';

import { Typography, Button, AccountLocation } from '@packages/ui/shared';
import { patchAuthUserProfile, useAuthDispatch, useAuthState } from '@packages/contexts/auth';
import { createUseStyles } from 'react-jss';
import colors from '@packages/core/styles/colors';
import Select from '@web/components/select';
import { useNavigate } from 'react-router-dom';
import { AccountStatusId } from '@packages/models/api';
import { authService, httpClient, storageService } from '@web/services/singletons';
import { WebStorageKeys } from '@packages/core/storage';
import { useAbortController } from '@packages/core/http';

const useAccountSettingsModalStyles = createUseStyles({
    welcome: {
        marginTop: 40,
        marginBottom: 16,
        padding: 16,
    },
    card: {
        backgroundColor: colors.white,
        boxShadow: `0px 1px 4px 0px rgba(0, 0, 0, 0.16)`,
    },
    section: {
        padding: 16,
    },
    footer: {
        borderTop: `1px solid ${colors.grayThree}`,
    },
});

const UserOnboarding: FC = () => {
    const classes = useAccountSettingsModalStyles();
    const { abortSignalRef } = useAbortController();
    const { t } = useTranslation();
    const { account } = useAuthState();
    const authDispatch = useAuthDispatch();
    const navigate = useNavigate();

    const [defaultPhoneId, setDefaultPhoneId] = useState(account?.defaultPhoneNumberId ?? '');
    const [defaultEmailId, setDefaultEmailId] = useState(account?.defaultEmailAddressId ?? '');

    const [acceptedToS, setAcceptedToS] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [canAcceptToS, setCanAcceptToS] = useState(false);
    const [showToS, setShowToS] = useState(false);

    useEffect(() => {
        if (!account) {
            return;
        }

        if (account.accountStatusId !== AccountStatusId.New) {
            storageService.getItem(WebStorageKeys.AuthRedirectUrl).then((authRedirect) => {
                if (authRedirect !== null && typeof authRedirect === 'object') {
                    return navigate({
                        ...authRedirect,
                        replace: true,
                        state: {
                            recentlyOnboarded: !!isSaving,
                        },
                    });
                }

                navigate(authRedirect || '/', {
                    replace: true,
                    state: {
                        recentlyOnboarded: !!isSaving,
                    },
                });
            });
        }
    }, [account, navigate, isSaving]);

    return (
        <div className="h-100 pb-6 overflow-auto">
            <TermsAndConditionsModal
                show={showToS}
                onHide={() => {
                    setShowToS(false);
                }}
            />

            <Row>
                <Col xs={12} md={{ offset: 2, span: 8 }} className={classes.welcome}>
                    <Typography variant="h3">
                        {t(
                            'screens:reviewProfile.welcome',
                            'Welcome {{firstName}}. Please review the following\r\ninformation before continuing.',
                            { firstName: account?.firstName }
                        )}
                    </Typography>
                </Col>

                {/* User Info */}
                <Col xs={12} md={{ offset: 2, span: 8 }} className={classes.card}>
                    <Row className={classes.section}>
                        <Col xs={6} className="mb-4">
                            <div>
                                <Typography variant="label">
                                    {t('views:onboarding.profile.username', 'Subarunet Username')}
                                </Typography>
                            </div>
                            <Typography variant="lead">{account?.soaUsername || 'N/A'}</Typography>
                        </Col>

                        <Col xs={6} className="mb-4">
                            <div>
                                <Typography variant="label">
                                    {t('views:onboarding.profile.fullName', 'Full Name')}
                                </Typography>
                            </div>
                            <Typography variant="lead">{account?.name || 'N/A'}</Typography>
                        </Col>

                        <Col xs={6} className="mb-4">
                            <div>
                                <Typography variant="label">
                                    {t('views:onboarding.profile.phoneNumber', 'Phone')}
                                </Typography>
                            </div>

                            {account && account.phoneNumbers.length > 1 ? (
                                <Select
                                    value={defaultPhoneId}
                                    options={account.phoneNumbers.map((pN) => ({
                                        title: pN.phoneNumberDescription,
                                        value: pN.phoneNumberId,
                                    }))}
                                    onChange={(event: React.ChangeEvent<HTMLSelectElement>) => {
                                        setDefaultPhoneId(event.target.value);
                                    }}
                                />
                            ) : (
                                <Typography variant="lead">
                                    {account?.phoneNumbers[0]?.phoneNumberDescription || 'N/A'}
                                </Typography>
                            )}
                        </Col>

                        <Col xs={6} className="mb-4">
                            <div>
                                <Typography variant="label">{t('views:onboarding.profile.email', 'Email')}</Typography>
                            </div>

                            {account && account.emailAddresses.length > 1 ? (
                                <Select
                                    value={defaultEmailId}
                                    options={account.emailAddresses.map((eA) => ({
                                        title: eA.emailAddress,
                                        value: eA.emailAddressId,
                                    }))}
                                    onChange={(event: React.ChangeEvent<HTMLSelectElement>) => {
                                        setDefaultEmailId(event.target.value);
                                    }}
                                />
                            ) : (
                                <Typography variant="lead">
                                    {account?.emailAddresses[0]?.emailAddress || 'N/A'}
                                </Typography>
                            )}
                        </Col>

                        <Col xs={12}>
                            {account?.locations.map((locationProfile, idx) => (
                                <AccountLocation
                                    key={idx}
                                    index={idx}
                                    showCount={account.locations.length > 1}
                                    elements={locationProfile.elements}
                                />
                            ))}
                        </Col>
                    </Row>
                </Col>

                {/* Terms and Conditions */}
                <Col xs={12} md={{ offset: 2, span: 8 }} className={classes.card}>
                    <Row className={classes.section}>
                        <Col xs={12} className="mb-4">
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                <Typography variant="label" style={{ fontSize: 24 }}>
                                    {t('views:onboarding.tos.title', 'Terms and Conditions')}
                                </Typography>

                                <Typography
                                    variant="caption"
                                    style={{
                                        fontSize: 14,
                                        marginTop: 4,
                                        color: colors.graySix,
                                    }}
                                >
                                    {t('views:onboarding.tos.subtitle', 'Please read the full agreement to proceed.')}
                                </Typography>
                            </div>

                            <div
                                style={{
                                    marginTop: '16px',
                                    maxHeight: '400px',
                                    overflowY: 'auto',
                                    padding: '16px',
                                    border: `1px solid ${colors.grayThree}`,
                                    borderRadius: '8px',
                                    backgroundColor: colors.grayTwo,
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word',
                                }}
                                onScroll={(e) => {
                                    const target = e.target as HTMLElement;
                                    const isScrolledToBottom =
                                        Math.abs(target.scrollHeight - target.scrollTop - target.clientHeight) < 2;

                                    if (isScrolledToBottom) {
                                        setCanAcceptToS(true);
                                    }
                                }}
                            >
                                {TERMS_AND_CONDITIONS.map((section, index) => (
                                    <div key={index} style={{ marginBottom: '24px' }}>
                                        {/* ToS Title */}
                                        {/* As per the ToS draft word doc, we italicize the first section for 'Effective Date' */}
                                        <Typography
                                            variant="h5"
                                            style={{
                                                fontWeight: 'bold',
                                                fontStyle: index === 0 ? 'italic' : 'normal',
                                                marginBottom: 8,
                                            }}
                                        >
                                            {section.title}
                                        </Typography>

                                        {/* Tos Body */}
                                        <Typography variant="default" style={{ lineHeight: 1.6 }}>
                                            {section.content}
                                        </Typography>
                                    </div>
                                ))}
                            </div>
                        </Col>
                    </Row>

                    <Row className={classNames(classes.section, classes.footer)}>
                        <Col xs={12}>
                            <div className="d-flex align-items-center justify-content-between">
                                <div className="d-flex align-items-center">
                                    <Form.Check
                                        className="mb-3"
                                        label={
                                            <Typography>
                                                {t(
                                                    'views:onboarding.tos.readAndAccept',
                                                    "I've read and accept the terms & conditions"
                                                )}
                                            </Typography>
                                        }
                                        id="tos"
                                        name="tos"
                                        checked={acceptedToS}
                                        onChange={() => {
                                            if (canAcceptToS) {
                                                setAcceptedToS(!acceptedToS);
                                            }
                                        }}
                                        disabled={!canAcceptToS} // Only disabled until fully scrolled.
                                    />
                                </div>
                                <div className="d-flex">
                                    <Button
                                        title={t('views:onboarding.cancel', 'Cancel')}
                                        onPress={() => {
                                            if (window.history.length > 1) {
                                                navigate(-1);
                                            } else {
                                                navigate('/');
                                            }
                                        }}
                                        style={{ marginRight: 8 }}
                                        variant="ghost-blue"
                                    />
                                    <Button
                                        title={t('views:onboarding.agree', 'I agree')}
                                        isLoading={isSaving}
                                        disabled={!acceptedToS} // We want to keep this disabled until the T&C are fully read.
                                        onPress={() => {
                                            if (!acceptedToS || !account || isSaving) {
                                                return;
                                            }
                                            setIsSaving(true);

                                            patchAuthUserProfile({
                                                authDispatch,
                                                authService,
                                                httpClient,
                                                accountId: account.accountId,
                                                accountPatch: {
                                                    accountStatusId: AccountStatusId.Active,
                                                },
                                                signal: abortSignalRef.current,
                                            }).catch((e) => {
                                                setIsSaving(false);
                                                alert(e.message);
                                            });
                                        }}
                                    />
                                </div>
                            </div>
                        </Col>
                    </Row>
                </Col>

                <Col xs={12} md={{ offset: 2, span: 8 }}>
                    <Row>
                        <Col xs={12} md={{ offset: 3, span: 6 }} className="mt-4 text-center">
                            <Typography variant="caption">
                                {account?.isRetailerUser
                                    ? t(
                                          'views:onboarding.retailerContactInfo',
                                          'If anything looks incorrect, contact your DEM admin (Dealer Employee Maintenance Administrator).'
                                      )
                                    : t(
                                          'views:onboarding.contactInfo',
                                          'If anything looks incorrect, contact the Subaru Helpdesk\r\nat 1-800-SOA-STAR (1-800-762-7827).'
                                      )}
                            </Typography>
                        </Col>
                    </Row>
                </Col>
            </Row>
        </div>
    );
};

export default UserOnboarding;
